import React, {
  useEffect, useState, useRef,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import NetworkIdForm from 'routes/NetworkId/containers/NetworkIdCreate/components/NetworkIdForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/networkId/networkId.selectors';
import * as actions from 'data/networkId/networkId.actions';
import * as types from 'data/networkId/networkId.types';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { NetworkId } from 'data/networkId/networkId.types';
import * as nodesSelectors from 'data/nodes/nodes.selectors';
import * as nodesActions from 'data/nodes/nodes.actions';
import networkIdTemplate from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/NetworkId.adapter';

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.NetworkId
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  networkIdTemplateDef: adaptTemplateVisibilityMode(networkIdTemplate, 'view'),
  deliveryFunctionId: state.global.selectedDF,
  selectedNetworkId: selectors.getSelectedNetworkId(state)[0],
  networkId: selectors.getNetworkId(state),
  nodes: nodesSelectors.getNodes(state),
});

const mapDispatch = {
  goToNetworkIdList: actions.goToNetworkIdList,
  getNetworkId: actions.getNetworkId,
  getNodes: nodesActions.getNodes,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const NetworkIdView = ({
  history,
  selectedNetworkId,
  networkId,
  deliveryFunctionId,
  networkIdTemplateDef,
  goToNetworkIdList,
  getNetworkId,
  getNodes,
  nodes,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<NetworkId>>({});
  const [template, setTemplate] = useState(networkIdTemplateDef);
  const { t } = useTranslation();

  const id = selectedNetworkId?.id;

  useEffect(() => {
    if (id) {
      getNetworkId(deliveryFunctionId, id);
    }
    getNodes(deliveryFunctionId);
  }, [deliveryFunctionId, id, getNetworkId, getNodes]);

  useEffect(() => {
    if (networkId != null && template != null) {
      const adaptedTemplate = adaptTemplateOptions(networkIdTemplateDef, nodes);
      if (adaptedTemplate) {
        adaptedTemplate.formId = Date.now().toString();
        adaptedTemplate.key = Date.now().toString();
      }
      setFormData(networkId);
      setTemplate(adaptedTemplate);
    }
  }, [
    id,
    networkId,
  ]);

  const handleCancel = () => goToNetworkIdList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as NetworkId);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = (section : string) => {
    if (formData && template) {
      return (
        <NetworkIdForm
          section={section}
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };

  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          isNewFormButtonsLayout
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="NetworkIdView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('networkId.view.title'))}
          <div className="read-only">
            {renderForm('main')}
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600 }}>{t('networkId.section.portRange')}</h2>
          {renderForm('portRange')}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(NetworkIdView));
